import {useLoggedInApi} from "../../../api";
import {useQuery, useMutation, useQueryClient} from "react-query";
import {toast} from "react-toastify";
import {chunkArray} from "../../../commonComponents/util";


export const usePaymentProjects = () => {
    const api = useLoggedInApi();



    const response = useQuery({
        queryKey: ['payment', 'projects'],
        queryFn: () => {
            const resource = api.resource('payment/projects');
            return resource.list().then(response => response.data);
        },
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: false
    })

    return {
        ...response,
        asList: response.data?.results,
        asDictionary: response.data?.results?.reduce((acc, project) => {
            acc[project.id.toString()] = project;
            return acc;
        }, {}),
        asCnDictionary: response.data?.results?.reduce((acc, project) => {
            acc[project.ac_id.toString()] = project;
            return acc;
        }, {})
    }
}


export const useIsLotCnExists = (cn) => {
    const api = useLoggedInApi();

    return useQuery({
        queryKey: ['payment', 'projects', 'exists', cn],
        queryFn: () => {
            const resource = api.resource('payment/lots');
            return resource.getItem('search', {params: {lot_cn: cn}}).then(response => response.data);
        },
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: false,
        enabled: !!cn && cn.trim() !== '' && cn.trim().length === 12,
    });
}

export const useImportProjects = () => {
    const api = useLoggedInApi();
    const queryClient = useQueryClient();

    return useMutation(
        (projects) => {
            const resource = api.resource('payment/projects');
            return resource.postAction("bulk_create", { data: projects });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['table', 'payment/projects']);
                queryClient.invalidateQueries(['payment', 'projects']);
                toast.success("Projects imported successfully");
            },
            onError: (error) => {
                console.error("Error importing projects:", error);
                toast.error("Failed to import projects. Please try again.");
            }
        }
    );
}

export const useImportLots = () => {
    const api = useLoggedInApi();
    const queryClient = useQueryClient();

    return useMutation(
        async ({ lots, onBatchProgress }) => {
            // Sort lots by lot_cn
            const sortedLots = [...lots].sort((a, b) => a.lot_cn.localeCompare(b.lot_cn));

            // Split into batches of 1000
            const batchSize = 1000;
            const batches = chunkArray(sortedLots, batchSize);

            // Process batches sequentially
            const results = [];
            const resource = api.resource('payment/lots');

            for (let i = 0; i < batches.length; i++) {
                // Update progress
                if (onBatchProgress) {
                    onBatchProgress({
                        current: i + 1,
                        total: batches.length
                    });
                }

                const batch = batches[i];
                const result = await resource.postAction("bulk_create", { data: batch });
                results.push(result);
            }

            return results;
        },
        {
            onSuccess: (results) => {
                queryClient.invalidateQueries(['table', 'payment/lots']);
                const totalImported = results.reduce((total, result) => total + (result.data?.imported_count || 0), 0);
                toast.success(`${totalImported} lots imported successfully`);
            },
            onError: (error) => {
                console.error("Error importing lots:", error);
                toast.error("Failed to import lots. Please try again.");
            }
        }
    );
}


export const usePaymentSettings = () => {
    const api = useLoggedInApi();

    return useQuery({
        queryKey: ['payment', 'settings'],
        queryFn: () => {
            const resource = api.resource('payment/settings/');
            return resource.list({
                params: {
                    page_size:100
                }
            }).then(response => response.data);
        },
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: false
    });
}


export const useProjectCount = () => {
    const api = useLoggedInApi();

    return useQuery({
        queryKey: ['payment', 'projects', 'count'],
        queryFn: () => {
            const resource = api.resource('payment/projects');
            return resource.getItem('count').then(response => response.data);
        },
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: false
    });
}

export const useLotCount = () => {
    const api = useLoggedInApi();

    return useQuery({
        queryKey: ['payment', 'lots', 'count'],
        queryFn: () => {
            const resource = api.resource('payment/lots');
            return resource.getItem('count').then(response => response.data);
        },
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: false
    });
}

export const useTransactionCount = () => {
    const api = useLoggedInApi();

    return useQuery({
        queryKey: ['payment', 'transactions', 'count'],
        queryFn: () => {
            const resource = api.resource('payment/transactions');
            return resource.getItem('count').then(response => response.data);
        },
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: false
    });
}
